// providers/AppContextProvider.tsx
"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import jwtDecode from "jwt-decode";
import { AppContext, useStateWithStorage } from "hooks";
import { useProfile } from "queries";
import { DecodedToken } from "types";

export { AppContext };

const AppContextProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [jwt, setJwtState] = useStateWithStorage(undefined, "o_a", {
    isJson: false,
  });

  const { data: profile, isLoading } = useProfile(jwt);
  const [modelId, setModelId] = useState<null | string>(null);

  const setJwt = (jwt: string | undefined) => {
    if (!jwt) {
      queryClient.removeQueries({ queryKey: ["useProfile"], exact: true });
    }

    setJwtState(jwt);
  };

  const logOut = () => {
    queryClient.invalidateQueries();
    setJwt(undefined);
    localStorage.removeItem("hasRedirectedToDashboard");
    router.replace("/");
  };

  useEffect(() => {
    if (jwt) {
      const decodedToken: DecodedToken = jwtDecode(jwt);

      if (decodedToken.exp < Date.now() / 1000) {
        logOut();
      }
    }
  }, [jwt]);

  return (
    <AppContext.Provider
      value={{
        jwt,
        setJwt,
        logOut,
        profile,
        isLoading,
        modelId,
        setModelId,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
